import { ApiError, type TaskSummary, type UpdateTaskInput } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { patchTaskInCache } from './taskUi';
import { invalidateTasks, patchTaskLists } from '@tradeoutlet/data';
import { useWorkspaceId } from '@/lib/hooks';

/**
 * Optimistic task update with optimistic locking: the server rejects stale `version`s with 409,
 * in which case we reload the task and tell the user rather than overwrite someone else's change.
 */
export function useUpdateTask() {
  const toast = useUi(s => s.toast);
  const ws = useWorkspaceId();
  return async (task: Pick<TaskSummary, 'id' | 'project_id' | 'version'>, patch: Omit<UpdateTaskInput, 'version'>) => {
    patchTaskInCache(task.project_id, { id: task.id, ...(patch as Partial<TaskSummary>) });
    try {
      const updated = await api.updateTask(task.id, { ...patch, version: task.version });
      patchTaskInCache(task.project_id, updated);
      queryClient.setQueryData(qk.task(task.id), updated);
      // Chat chips for this task (realtime also invalidates them; this covers polling mode).
      if (updated.links?.length) void queryClient.invalidateQueries({ queryKey: ['task-links'] });
      // t197u: every list the task is in (Tasks area open / done, My tasks, Today, badges) moves it now, then refetches.
      patchTaskLists(queryClient, updated as Parameters<typeof patchTaskLists>[1]);
      void invalidateTasks(queryClient, ws, task.id);
      return updated;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) toast('Someone else just changed this task - reloaded the latest version.', 'error');
      else toast(errorMessage(e), 'error');
      void queryClient.invalidateQueries({ queryKey: qk.tasks(task.project_id) });
      void queryClient.invalidateQueries({ queryKey: qk.task(task.id) });
      return null;
    }
  };
}
